// Functions to process form answers and submit the sellers packet.
import { Schema } from 'tiny-validation';
import { awsUpload } from '../../components/common/FileUpload/lib';
import { ProgressMessage } from '../../components/common/FileUpload/UploadProgressModal';
import { api, tartleClient } from '../../lib/api';
import { logger } from '../../lib/logger';
import { Packet, PacketQuestion } from '../../types/packet.schema';
import {
  Attachment,
  PacketAnswer,
  SellersPacket
} from '../../types/sellersPacket.schema';
import { splitReducer } from '../../utils';
import {
  FieldFiles,
  PacketFormFieldValue,
  PacketFormValues
} from './useSellersPacketForm';
import { getPacketStatus, getQuestionId, validatePartially } from './formUtils';
import {
  isCheckboxGroupField,
  isDateField,
  isFileInputField,
  isTextBasedField
} from './typeguards';
import { track } from '../../lib/analytics';
import { FileInputValue } from '../UseFileInput';
import { encrypt } from '../../lib/encryption/asymmetric';

export const submitPacket = async ({
  submitType,
  packet,
  requiresEncryption,
  formValues,
  updateProgress,
  toggleUploadProgressModal,
  previousStatus,
  schema,
  pubkey,
  authToken,
  // This is only used for partially completed frictionless packet.
  answersToAppend = [],
  isFrictionless = false
}: {
  submitType: 'save' | 'publish' | 'save-configuration';
  packet: Packet;
  requiresEncryption: boolean;
  formValues: PacketFormValues;
  updateProgress: (progress: ProgressMessage) => void;
  toggleUploadProgressModal: () => void;
  previousStatus: 'published' | 'unpublished';
  schema: Schema;
  pubkey: string;
  authToken?: string;
  answersToAppend?: PacketAnswer[];
  isFrictionless?: boolean;
}): Promise<SellersPacket> => {
  if (checkForDuplicateFiles(formValues)) {
    return Promise.reject(['Duplicate files are not allowed.']);
  }

  const log = logger(`${submitType}packet`);
  const isIntegration = submitType === 'save-configuration';

  // If we are doing a save, first we have to validate the partial packet.
  if (submitType === 'save') {
    const errors = validatePartially(formValues, schema);
    if (errors !== null) {
      log.info('Partial validation errors', errors);
      return Promise.reject({ errors: ['Please correct errors marked in red above'] });
    }
  }

  const { autosell, donation, ...fieldRecords } = formValues;
  const sellersPacket: SellersPacket = {
    beneficiaryAccountId: donation ?? null,
    autosellSetting: autosell ? 'enabled_for_any_price' : 'disabled',
    status: getPacketStatus(isIntegration, previousStatus, submitType)
  };

  if (!isIntegration) {
    sellersPacket['answers'] = [];
    const fields = Object.entries(fieldRecords).filter(([, value]) => value !== '');
    const newFiles: FieldFiles = [];

    for (const [fieldId, value] of fields) {
      const questionId = getQuestionId(fieldId);
      const question = packet.questions.find(question => question.id === questionId);

      if (!question) {
        throw new Error(`Internal Error: question ${questionId} not found`);
      }

      const { filesToUpload, answers } = getPacketAnswersFromFormField(
        value,
        question,
        packet.id
      );

      if (question.encrypted) {
        for (const answer of answers) {
          if (question.encrypted) {
            answer.value = encrypt(answer.value, pubkey);
          }
        }
      }

      sellersPacket.answers.push(...answers);
      if (filesToUpload) {
        newFiles.push(...filesToUpload);
      }
    }

    if (newFiles.length > 0) {
      let attachments: Attachment[] = [];
      try {
        attachments = await awsUpload({
          // Only moderators can set the videoFiles property.
          createAttachmentEndpoint: api.seller.createSellersPacketAttachment,
          createAttachmentEndpointParams: [packet.id],
          toggleUploadProgressModal,
          updateProgress,
          files: newFiles
        });
      } catch (error) {
        return Promise.reject({ errors: [`Error uploading file`] });
      }
      for (let i = 0; i < attachments.length; i++) {
        sellersPacket.answers.push({
          questionId: newFiles[i].questionId,
          value: attachments[i].signedId
        });
      }
    }
  }

  if (isFrictionless) {
    sellersPacket.answers.push(...answersToAppend);
  }

  if (requiresEncryption) {
    sellersPacket.autosellSetting = 'disabled';
  }

  return tartleClient({
    method: 'PUT',
    endpoint: api.seller.sellersPacket,
    endpointParams: [packet.id],
    authToken,
    data: sellersPacket
  }).then(() => {
    if (submitType === 'publish') {
      track('packetPublish', ['tartle'], { packetId: packet.id, isFrictionless });
    } else if (submitType === 'save') {
      track('packetSave', ['tartle'], { packetId: packet.id, isFrictionless });
    }
    return sellersPacket;
  });
};

const getPacketAnswersFromFormField = (
  value: PacketFormFieldValue,
  question: PacketQuestion,
  packetId: string
): {
  filesToUpload?: FieldFiles;
  answers: {
    questionId: string;
    fillMatchingQuestions: boolean;
    value: string;
  }[];
} => {
  // Temporary replacement of multiselects with checkboxes
  if (question.htmlInputType === 'select' && question.multiple) {
    question.htmlInputType = 'checkbox';
  }

  if (isFileInputField(question, value)) {
    return processFileInputAnswers(value, question.id);
  }

  if (isCheckboxGroupField(question, value)) {
    return processCheckboxAnswers(question, value);
  }

  if (isDateField(question, value)) {
    return processDateAnswer(question, value);
  }

  if (isTextBasedField(question, value)) {
    return processTextAnswer(question, value);
  }

  throw new Error(
    `Unexpected field in packet ${packetId} question ${question.id}: ${
      question.htmlInputType
    } - ${value} (${typeof value})`
  );
};

/* Functions to process answers for each type of field, note the special case used for returning new files found */

const processCheckboxAnswers = (question: PacketQuestion, value: boolean[]) => ({
  answers: question.boundedChoices
    .filter((_, index) => value[index])
    .map(choice => ({
      questionId: question.id,
      fillMatchingQuestions: true,
      value: choice.text
    }))
});

const processFileInputAnswers = (value: FileInputValue[], questionId: string) => {
  const [newFiles, oldFiles] = value.reduce(
    splitReducer(file => file.local),
    [[], []]
  );
  return {
    filesToUpload: newFiles.map(file => ({ ...file, questionId })),
    answers: oldFiles.map(fileInputValue => ({
      questionId,
      fillMatchingQuestions: true,
      value: fileInputValue.signedId
    }))
  };
};

const processDateAnswer = (question: PacketQuestion, value: Date | '') => {
  if (value === '') {
    throw new Error(`Unexpected empty date value for ${question.id}`);
  }
  return {
    answers: [
      {
        questionId: question.id,
        fillMatchingQuestions: true,
        value: value.toISOString()
      }
    ]
  };
};

const processTextAnswer = (question: PacketQuestion, value: string) => ({
  answers:
    value === ''
      ? []
      : [
          {
            questionId: question.id,
            fillMatchingQuestions: true,
            value
          }
        ]
});

const checkForDuplicateFiles = (values: PacketFormValues) => {
  const checksums = new Set<string>();
  for (const fieldAnswer of Object.values(values)) {
    if (isFileInputValues(fieldAnswer)) {
      for (const fileInputValue of fieldAnswer) {
        if (checksums.has(fileInputValue.checksum)) {
          return true;
        }
        checksums.add(fileInputValue.checksum);
      }
    }
  }
  return false;
};

export const isFileInputValues = (value: unknown): value is FileInputValue[] =>
  value &&
  Array.isArray(value) &&
  value.length > 0 &&
  value[0] !== null &&
  typeof value[0] === 'object' &&
  'checksum' in value[0];
